import { Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { ConfirmationService, MessageService } from 'primeng/api';
import { fromEvent, interval } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { UnsavedChangesStateService } from './unsaved-changes-state.service';

type PendingAppUpdate = {
  message: string;
  apply: () => Promise<void>;
};

/**
 * Owns both update-detection channels (Angular service worker VERSION_READY
 * events, and a deployed-version.json poll that also catches non-SW clients)
 * and the single "Update Application" confirm prompt they share. Initialized
 * once from AppComponent; also exposes checkForUpdatesNow() for a manual
 * "Check for Updates" menu action.
 */
@Injectable({
  providedIn: 'root',
})
export class AppUpdateService {
  private readonly deployedVersionPath = 'version.json';
  private activeDeployedVersion: string | null = null;
  private updatePromptDeferredForSession = false;
  private updateDialogOpen = false;
  private unsavedUpdateNoticeShown = false;
  private pendingAppUpdate: PendingAppUpdate | null = null;
  private initialized = false;

  constructor(
    private readonly updates: SwUpdate,
    private readonly confirmationService: ConfirmationService,
    private readonly messageService: MessageService,
    private readonly unsavedChangesState: UnsavedChangesStateService,
  ) {}

  initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.unsavedChangesState.hasUnsavedChanges$
      .pipe(distinctUntilChanged())
      .subscribe((hasUnsavedChanges) => {
        if (!hasUnsavedChanges) {
          this.unsavedUpdateNoticeShown = false;
          this.presentPendingUpdatePrompt();
        }
      });

    if (this.updates.isEnabled) {
      this.setupServiceWorkerUpdates();
    }

    // Independent of the service worker, so a build/environment without one still gets update prompts.
    if (this.deployedVersionPollingApplies()) {
      this.setupDeployedVersionUpdates();
    }
  }

  /** The dev server (`ng serve`) has no deployed version.json, so polling for it there only adds noise. */
  private deployedVersionPollingApplies(): boolean {
    return !['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);
  }

  /** Manually triggered (e.g. from a "Check for Updates" menu item). Always gives feedback. */
  async checkForUpdatesNow(): Promise<void> {
    if (!this.updates.isEnabled && !this.deployedVersionPollingApplies()) {
      this.messageService.add({
        severity: 'info',
        summary: 'Updates unavailable',
        detail: 'Update checking is not available in this environment (service worker is disabled).',
      });
      return;
    }

    this.updatePromptDeferredForSession = false;

    await Promise.allSettled([
      this.updates.isEnabled
        ? this.updates.checkForUpdate().catch((err) => {
            console.warn('[SW] Manual update check failed', err);
          })
        : Promise.resolve(),
      this.checkDeployedVersion('manual'),
    ]);

    // Give a VERSION_READY event (if any) a brief moment to land before deciding.
    await new Promise((resolve) => setTimeout(resolve, 500));

    if (this.pendingAppUpdate) {
      this.presentPendingUpdatePrompt();
    } else {
      this.messageService.add({
        severity: 'success',
        summary: 'Up to date',
        detail: "You're running the latest version.",
      });
    }
  }

  private setupServiceWorkerUpdates(): void {
    this.updates.versionUpdates.subscribe((event) => {
      if (event.type === 'VERSION_READY') {
        const readyEvent = event as VersionReadyEvent;
        console.log('[SW] New version ready', {
          current: readyEvent.currentVersion.hash,
          latest: readyEvent.latestVersion.hash,
        });

        this.queueAppUpdate(
          'A new version is available. Reload now to update?',
          async () => {
            await this.updates.activateUpdate();
            document.location.reload();
          },
        );
      }
    });

    // Check immediately on startup and once again after SW registration settles.
    this.checkForUpdates('startup');
    setTimeout(() => this.checkForUpdates('startup-delayed'), 10000);

    interval(5 * 60 * 1000).subscribe(() => {
      this.checkForUpdates('interval');
    });

    fromEvent(window, 'focus').subscribe(() => this.checkForUpdates('focus'));

    fromEvent(document, 'visibilitychange')
      .pipe(filter(() => document.visibilityState === 'visible'))
      .subscribe(() => this.checkForUpdates('visibilitychange'));

  }

  /** Secondary update channel: bypass the SW and compare the deployed version marker. */
  private setupDeployedVersionUpdates(): void {
    this.initializeDeployedVersionTracking();
    interval(60 * 1000).subscribe(() => this.checkDeployedVersion('interval'));
    fromEvent(window, 'focus').subscribe(() => this.checkDeployedVersion('focus'));
    fromEvent(document, 'visibilitychange')
      .pipe(filter(() => document.visibilityState === 'visible'))
      .subscribe(() => this.checkDeployedVersion('visibilitychange'));
  }

  private checkForUpdates(source: string): void {
    this.updates.checkForUpdate().catch((err) => {
      console.warn(`[SW] Update check failed (${source})`, err);
    });
  }

  private initializeDeployedVersionTracking(): void {
    this.readCurrentDeployedVersion().then((version) => {
      this.activeDeployedVersion = version;
    });
  }

  private async checkDeployedVersion(source: string): Promise<void> {
    const version = await this.readCurrentDeployedVersion();
    if (!version) {
      return;
    }

    if (!this.activeDeployedVersion) {
      this.activeDeployedVersion = version;
      return;
    }

    if (version !== this.activeDeployedVersion) {
      console.log('[Version] New deployment detected. Reloading app.', {
        previous: this.activeDeployedVersion,
        next: version,
        source,
      });
      this.activeDeployedVersion = version;
      this.queueAppUpdate(
        'A new deployment is available. Reload now to update?',
        async () => {
          document.location.reload();
        },
      );
    }
  }

  private queueAppUpdate(message: string, apply: () => Promise<void>): void {
    this.pendingAppUpdate = { message, apply };
    this.presentPendingUpdatePrompt();
  }

  private presentPendingUpdatePrompt(): void {
    if (!this.pendingAppUpdate || this.updateDialogOpen || this.updatePromptDeferredForSession) {
      return;
    }

    if (this.unsavedChangesState.hasUnsavedChanges()) {
      this.showUnsavedChangesUpdateNotice();
      return;
    }

    const pendingUpdate = this.pendingAppUpdate;
    this.updateDialogOpen = true;
    this.confirmationService.confirm({
      header: 'Update Application',
      message: pendingUpdate.message,
      icon: 'pi pi-refresh',
      acceptLabel: 'Update now',
      rejectLabel: 'Later',
      accept: () => {
        this.updateDialogOpen = false;
        this.pendingAppUpdate = null;
        void pendingUpdate.apply().catch((err) => {
          console.warn('[Update] Failed to apply queued update', err);
        });
      },
      reject: () => {
        this.updateDialogOpen = false;
        this.pendingAppUpdate = null;
        this.updatePromptDeferredForSession = true;
      },
    });
  }

  private showUnsavedChangesUpdateNotice(): void {
    if (this.unsavedUpdateNoticeShown || this.updateDialogOpen) {
      return;
    }

    this.unsavedUpdateNoticeShown = true;
    this.updateDialogOpen = true;
    this.confirmationService.confirm({
      header: 'Update Ready',
      message:
        'A new version is ready, but you have unsaved changes. Save or discard them first, and the update prompt will appear again once the page is safe to reload.',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'OK',
      rejectVisible: false,
      accept: () => {
        this.updateDialogOpen = false;
      },
      reject: () => {
        this.updateDialogOpen = false;
      },
    });
  }

  private async readCurrentDeployedVersion(): Promise<string | null> {
    const versionUrl = new URL(this.deployedVersionPath, document.baseURI);
    versionUrl.searchParams.set('ngsw-bypass', 'true');
    versionUrl.searchParams.set('t', String(Date.now()));

    try {
      // Bounded timeout: without it, a fully unreachable server (e.g. powered off, as
      // opposed to actively refusing connections) can leave this fetch pending far longer
      // than this background version check should ever take.
      const abortController = new AbortController();
      const timeoutId = setTimeout(() => abortController.abort(), 10_000);
      let response: Response;
      try {
        response = await fetch(versionUrl.toString(), {
          cache: 'no-store',
          signal: abortController.signal,
        });
      } finally {
        clearTimeout(timeoutId);
      }
      if (!response.ok) {
        return null;
      }

      const payload = (await response.json()) as { version?: string };
      const version = String(payload?.version ?? '').trim();
      return version || null;
    } catch (err) {
      console.warn('[Version] Could not read deployment version marker', err);
      return null;
    }
  }
}
